import { type ReactNode, useMemo } from 'react';
import { resolveOverflowPanelProps } from './overlay-panel.utils';
import {
    createOverflowItemTemplate,
    createOverflowValueTemplate,
} from './OverflowTemplate.utils';
import { getLabelText, isPlainLabelList } from './plain-label.utils';
import { type Maybe } from './maybe';

type OptionTemplate =
    | ReactNode
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | ((option: unknown, ...args: any[]) => ReactNode);

export interface SelectOverflowBehaviorInput {
    itemTemplate?: Maybe<OptionTemplate>;
    valueTemplate?: Maybe<OptionTemplate>;
    optionGroupChildren?: string;
    optionLabel?: string;
    options?: Maybe<unknown[]>;
    panelClassName?: string;
    wrapItems?: boolean;
}

export interface SelectOverflowBehavior {
    resolvedPanelClassName: string | undefined;
    itemTemplate: OptionTemplate | undefined;
    valueTemplate: OptionTemplate | undefined;
}

export function useSelectOverflowBehavior({
    itemTemplate,
    valueTemplate,
    optionGroupChildren,
    optionLabel,
    options,
    panelClassName,
    wrapItems,
}: SelectOverflowBehaviorInput): SelectOverflowBehavior {
    return useMemo(() => {
        const getOptionLabel = (option: unknown): Maybe<string> =>
            getLabelText(option, optionLabel ?? 'label');
        const enabled =
            !itemTemplate &&
            !valueTemplate &&
            !optionGroupChildren &&
            isPlainLabelList(options, getOptionLabel);

        const { resolvedPanelClassName } = resolveOverflowPanelProps({
            panelClassName,
            shouldUseOverflow: enabled,
            wrapItems,
        });

        return {
            resolvedPanelClassName,
            itemTemplate: createOverflowItemTemplate({
                enabled,
                getLabel: getOptionLabel,
                className: 'ics-overflow-label',
                fallbackTemplate: itemTemplate,
            }),
            valueTemplate: createOverflowValueTemplate({
                enabled,
                getLabel: getOptionLabel,
                className: 'ics-overflow-label',
                fallbackTemplate: valueTemplate,
                renderEmpty: ({ placeholder }): ReactNode => (
                    <span>{placeholder}</span>
                ),
            }),
        } as SelectOverflowBehavior;
    }, [
        itemTemplate,
        valueTemplate,
        optionGroupChildren,
        optionLabel,
        options,
        panelClassName,
        wrapItems,
    ]);
}
